import { useState, useEffect, useRef, useCallback } from "react";
import type { SelectChangeEvent } from "@mui/material";

interface UseFormDialogProps<T extends Record<string, any>> {
  initialData: T;
  onSubmit: (data: T) => Promise<void>;
  onClose: () => void;
  validate?: (data: T) => string[];
}

export function useFormDialog<T extends Record<string, any>>({
  initialData,
  onSubmit,
  onClose,
  validate,
}: UseFormDialogProps<T>) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<T>(initialData);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const prevInitialDataRef = useRef<T>(initialData);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
    },
    [],
  );

  const validateForm = useCallback((): boolean => {
    if (!validate) return true;

    const errors = validate(formData);
    setValidationErrors(errors);
    return errors.length === 0;
  }, [validate, formData]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!validateForm()) return;

      setLoading(true);
      try {
        await onSubmit(formData);
        onClose();
      } catch (error) {
        // Error is handled by the parent component
        // Form stays open with error message
      } finally {
        setLoading(false);
      }
    },
    [formData, onSubmit, onClose, validateForm],
  );

  const handleClose = useCallback(() => {
    setFormData(initialData);
    setValidationErrors([]);
    onClose();
  }, [initialData, onClose]);

  // Reset form when dialog opens/closes
  useEffect(() => {
    // Only reset if initialData has actually changed
    if (JSON.stringify(prevInitialDataRef.current) !== JSON.stringify(initialData)) {
      setFormData(initialData);
      setValidationErrors([]);
      prevInitialDataRef.current = initialData;
    }
  }, [initialData]);

  return {
    loading,
    formData,
    validationErrors,
    handleChange,
    handleSubmit,
    handleClose,
    setFormData,
  };
}
